import { useModal } from 'components/Modals';
import { createContext, UIEvent, useContext, useState } from 'react';
import { Enrolment } from 'services/resources';
import colors from 'styles/colors';
import FlashMessage from 'components/FlashMessage';
import { FLASH_MESSAGE_TYPES } from 'constants/common';
import { RemoveModal } from 'components/Modals/Remove';
import { useIntl } from 'react-intl';
import { useAppDispatch } from 'hooks/store';
import { removeEnrolment } from 'services/Telematics/thunks';

const UnenrollmentContext = createContext<{
  openModal: (event: UIEvent) => void;
  setEnrolment: (enrolment: Enrolment | null) => void;
  enrolment: Enrolment | null;
}>(undefined);

const UnenrollmentModalProvider = ({ children }) => {
  const { formatMessage } = useIntl();
  const { openModal, closeModal, Modal, isOpen } = useModal({
    background: colors.modalBackground,
    programmaticallyOpen: true,
    closeOnOutsideClick: false,
  });

  const dispatch = useAppDispatch();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);

  const [flashMessageVisible, setFlashMessageVisible] = useState(false);
  const [flashMessageType, setFlashMessageType] = useState<FLASH_MESSAGE_TYPES | null>(null);
  const [flashMessage, setFlashMessage] = useState<string | null>(null);

  const addFlashMessage = (text: string, type: FLASH_MESSAGE_TYPES) => {
    setFlashMessageVisible(true);
    setFlashMessageType(type);
    setFlashMessage(text);
  };

  return (
    <UnenrollmentContext.Provider value={{ openModal, setEnrolment, enrolment }}>
      {children}
      {isOpen && (
        <Modal>
          <RemoveModal
            removedItemTitle={formatMessage({ id: 'views.FleetSettings.OEMTelematics.Modals.Unenroll.title' })}
            removedItemDescription={formatMessage({
              id: 'views.FleetSettings.OEMTelematics.Modals.Unenroll.description',
            })}
            confirmButtonLabel={formatMessage({ id: 'views.FleetSettings.OEMTelematics.Modals.Unenroll.confirm' })}
            confirmCallback={async (e: UIEvent) => {
              try {
                await dispatch(removeEnrolment(enrolment.id));
                addFlashMessage(
                  formatMessage({ id: 'views.FleetSettings.OEMTelematics.Modals.Unenroll.success' }),
                  FLASH_MESSAGE_TYPES.success
                );
                closeModal(e);
              } catch (error) {
                addFlashMessage(
                  formatMessage({ id: 'views.FleetSettings.OEMTelematics.Modals.Unenroll.error' }),
                  FLASH_MESSAGE_TYPES.error
                );
              }
            }}
            cancelCallback={closeModal}
          />
        </Modal>
      )}
      <FlashMessage
        visible={flashMessageVisible}
        type={flashMessageType}
        text={flashMessage}
        onClose={() => {
          setFlashMessageVisible(false);
        }}
      />
    </UnenrollmentContext.Provider>
  );
};

export const useUnenrollment = () => {
  const context = useContext(UnenrollmentContext);
  if (!context) {
    throw new Error('useUnenrollment must be used within a UnenrollmentModalProvider');
  }

  return context;
};

export default UnenrollmentModalProvider;
